<template>
  <Card class="waterfall-tender-rate-quote-card no-pad" draggable>
    <template #content>
      <div class="waterfall-tender-rate-quote-card-row">
        <div class="waterfall-tender-rate-quote-card-carrier">
          <i
            v-if="!excluded"
            aria-hidden="true"
            class="pi pi-bars drag-handle"
          />

          <div v-if="!excluded" class="flex flex-column">
            <RygenButton
              class="move-button"
              mode="tertiary"
              icon="pi pi-chevron-up"
              :aria-label="t('waterfallTender.moveUp')"
              :disabled="!enableUpArrow"
              @click="onMoveUp"
            />
            <RygenButton
              class="move-button"
              mode="tertiary"
              icon="pi pi-chevron-down"
              :aria-label="t('waterfallTender.moveDown')"
              :disabled="!enableDownArrow"
              @click="onMoveDown"
            />
          </div>

          <CarrierAvatar
            :carrier="rateQuote.carrier"
            :accent-color="accentColor"
          />

          <div class="carrier-name-container flex flex-column">
            <p class="waterfall-tender-rate-quote-card-carrier-name">
              {{ rateQuote.carrier?.name }}
              {{ rateQuote.carrier?.scac ? `(${rateQuote.carrier.scac})` : '' }}
            </p>
            <small v-if="exclusionReason" class="text-gray-darker uppercase">
              {{
                translateEnum(
                  exclusionReason,
                  'WaterfallTenderRateQuoteExclusionReason',
                )
              }}
            </small>
          </div>

          <RateQuoteWarnings
            v-if="!exclusionReason"
            :rate-quote="rateQuote"
            mode="CHIP"
          />
        </div>

        <div
          class="waterfall-tender-rate-quote-card-highlights flex flex-column flex-gap-2"
        >
          <div class="key-value-pairs flex">
            <div class="waterfall-tender-rate-quote-card-highlights-cell">
              <KeyValuePair
                class="w-full"
                :label="t('rateShop.service')"
                :value="titleCase(rateQuote.serviceType)"
                uppercase-label
              />
            </div>

            <div class="waterfall-tender-rate-quote-card-highlights-cell">
              <KeyValuePair
                :label="t('rateShop.transit')"
                :value="
                  rateQuote.transitTime
                    ? t('rateShop.countDays', { count: rateQuote.transitTime })
                    : '-'
                "
                uppercase-label
              />
            </div>

            <div class="waterfall-tender-rate-quote-card-highlights-cell">
              <KeyValuePair
                :label="t('shipments.modes')"
                :value="
                  rateQuote.mode ? translateEnum(rateQuote.mode, 'Mode') : '-'
                "
                uppercase-label
              />
            </div>

            <div class="waterfall-tender-rate-quote-card-highlights-cell">
              <KeyValuePair
                class="waterfall-tender-rate-quote-card-rate"
                :class="{ 'has-rate': rateQuote.totalPrice !== undefined }"
                :label="t('rateShop.rates')"
                :value="
                  renderMoney(rateQuote.totalPrice, {
                    currencyCode: rateQuote.currencyCode,
                  })
                "
                uppercase-label
              />
            </div>
          </div>

          <div
            v-if="!excluded && !isBrokerageQuote"
            class="tender-config-options flex flex-gap-1"
          >
            <InputWrapper
              v-slot="{ ariaProps, inputId }"
              :input-id="`waterfall-tender-rate-quote-${rateQuote.id}-methods`"
              class="w-6"
              :label="t('shipments.tenderMethods')"
            >
              <RygenDropdown
                :id="inputId"
                v-model="internalTenderMethod"
                v-bind="ariaProps"
                :options="availableTenderMethods"
                class="flex-grow-1"
                :disabled="availableTenderMethods.length === 1"
                :placeholder="t('shipments.tenderMethods')"
              />
            </InputWrapper>

            <InputWrapper
              v-if="tenderMethod === TenderMethod.EMAIL"
              v-slot="{ ariaProps, errors, inputClasses, inputId, invalid }"
              :errors="v$.emails.$errors"
              :input-id="`waterfall-tender-rate-quote-${rateQuote.id}-emails`"
              class="w-6"
              :label="t('shipments.emailsCommaSeparated')"
            >
              <InputText
                :id="inputId"
                v-model="internalTenderConfig.emails"
                v-bind="ariaProps"
                :class="[
                  inputClasses,
                  {
                    'input-warning':
                      !errors.length && !internalTenderConfig?.emails?.length,
                  },
                ]"
                :invalid="invalid"
                :placeholder="t('shipments.emailsCommaSeparated')"
                :disabled="emailDisabled"
              />
            </InputWrapper>
          </div>
        </div>
      </div>

      <SendToBrokerageSection
        v-if="!excluded && isBrokerageQuote"
        :id="`waterfall-tender-${rateQuote.id}-brokerage`"
        v-model="internalSendToBrokerageRequest"
        :carrier-setting="brokerCarrierSetting"
        :carriers="sendToBrokerageCarriers"
        :equipment-types="equipmentTypes"
        :service-levels="serviceLevels"
      />
    </template>
  </Card>
</template>

<script lang="ts" setup>
import isEqual from 'lodash/isEqual';
import { computed, PropType, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { requiredIf, useValidations } from '@rygen/rygen-ui/composables';
import { renderMoney, titleCase, translateEnum } from '@rygen/rygen-ui/i18n';
import {
  CapacityProviderType,
  CarrierSetting,
  EquipmentType,
  Loadable,
  SendToBrokerageRequest,
  ServiceLevel,
  TenderConfigOptions,
  TenderMethod,
} from '@rygen/rygen-ui/model';

import { useCorsairPermissions } from '@/composables';
import { Carrier, RateQuote } from '@/model';
import { useCurrentUserStore } from '@/stores/CurrentUser';

import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import CarrierAvatar from '@rygen/rygen-ui/components/carrier/CarrierAvatar.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import RygenDropdown from '@rygen/rygen-ui/components/form/RygenDropdown.vue';
import SendToBrokerageSection from '@rygen/rygen-ui/components/routingGuide/SendToBrokerageSection.vue';
import KeyValuePair from '@rygen/rygen-ui/components/ui/KeyValuePair.vue';
import Card from 'primevue/card';
import InputText from 'primevue/inputtext';

import RateQuoteWarnings from './RateQuoteWarnings.vue';

const props = defineProps({
  rateQuote: {
    type: RateQuote,
    default: () => new RateQuote(),
  },
  tenderMethod: {
    type: String as PropType<TenderMethod>,
    default: () => undefined,
  },
  tenderConfig: {
    type: TenderConfigOptions,
    default: () => undefined,
  },
  exclusionReason: {
    type: String,
    default: () => undefined,
  },
  excluded: {
    type: Boolean,
    default: () => false,
  },
  enableUpArrow: {
    type: Boolean,
    default: () => false,
  },
  enableDownArrow: {
    type: Boolean,
    default: () => false,
  },
  availableTenderMethods: {
    type: Array as PropType<TenderMethod[]>,
    default: () => [],
  },
  sendToBrokerageRequest: {
    type: Object as PropType<SendToBrokerageRequest>,
    default: () => undefined,
  },
  brokerCarrierSetting: {
    type: Object as PropType<CarrierSetting>,
    default: () => undefined,
  },
  sendToBrokerageCarriers: {
    type: Object as PropType<Loadable<Carrier[]>>,
    default: () => ({ loading: false }),
  },
  equipmentTypes: {
    type: Object as PropType<Loadable<EquipmentType[]>>,
    default: () => ({ loading: false }),
  },
  serviceLevels: {
    type: Object as PropType<Loadable<ServiceLevel[]>>,
    default: () => ({ loading: false }),
  },
});
const {
  rateQuote,
  tenderMethod,
  tenderConfig,
  exclusionReason,
  excluded,
  enableUpArrow,
  enableDownArrow,
  availableTenderMethods,
  sendToBrokerageRequest,
  brokerCarrierSetting,
  sendToBrokerageCarriers,
  equipmentTypes,
  serviceLevels,
} = toRefs(props);
const emit = defineEmits<{
  (event: 'update:tender-config', tenderConfig: TenderConfigOptions): void;
  (event: 'update:tender-method', tenderMethod: TenderMethod): void;
  (
    event: 'update:send-to-brokerage-request',
    request: SendToBrokerageRequest,
  ): void;
  (event: 'move-down', rateQuote: RateQuote): void;
  (event: 'move-up', rateQuote: RateQuote): void;
}>();

const { accentColor } = useCurrentUserStore();
const { t } = useI18n();
const { shouldDisableTenderEmail } = useCorsairPermissions();

const internalTenderConfig = ref(
  props.tenderConfig ?? new TenderConfigOptions(),
);

const internalTenderMethod = ref(props.tenderMethod);

const internalSendToBrokerageRequest = ref(
  props.sendToBrokerageRequest ?? new SendToBrokerageRequest(),
);

const { v$ } = useValidations(internalTenderConfig, {
  emails: {
    required: requiredIf(
      () =>
        excluded.value === false && tenderMethod?.value === TenderMethod.EMAIL,
      t('common.email'),
    ),
  },
});

watch(
  () => tenderConfig?.value,
  (newVal: TenderConfigOptions | undefined) => {
    internalTenderConfig.value = new TenderConfigOptions(newVal);
  },
);

watch(internalTenderConfig, (newVal: TenderConfigOptions) => {
  if (isEqual(internalTenderConfig.value, newVal)) {
    return;
  }

  emit('update:tender-config', newVal);
});

watch(
  () => tenderMethod?.value,
  (newVal: TenderMethod | undefined) => {
    internalTenderMethod.value = newVal;
  },
);

watch(internalTenderMethod, (newVal: TenderMethod | undefined) => {
  if (newVal === undefined) {
    return;
  }

  emit('update:tender-method', newVal);
});

watch(
  () => sendToBrokerageRequest?.value,
  (newVal: SendToBrokerageRequest | undefined) => {
    if (isEqual(internalSendToBrokerageRequest.value, newVal)) {
      return;
    }
    internalSendToBrokerageRequest.value = new SendToBrokerageRequest(newVal);
  },
);

watch(internalSendToBrokerageRequest, (newVal: SendToBrokerageRequest) => {
  if (isEqual(sendToBrokerageRequest?.value, newVal)) {
    return;
  }
  emit('update:send-to-brokerage-request', newVal);
});

const isBrokerageQuote = computed(
  () => rateQuote.value.carrier?.type === CapacityProviderType.BROKER,
);

const emailDisabled = computed((): boolean => {
  return shouldDisableTenderEmail(rateQuote.value.carrier?.type);
});

const onMoveUp = () => {
  if (enableUpArrow.value) {
    emit('move-up', rateQuote.value);
  }
};

const onMoveDown = () => {
  if (enableDownArrow.value) {
    emit('move-down', rateQuote.value);
  }
};
</script>

<style>
.waterfall-tender-rate-quote-card.p-card {
  container: waterfall-tender-rate-quote / inline-size;

  .p-card-content {
    display: flex;
    flex-direction: column;
    gap: var(--gap-1);
    padding: var(--sp-3);

    .waterfall-tender-rate-quote-card-row {
      display: flex;
      gap: var(--gap-1);
      justify-content: space-between;
      align-items: center;
    }

    .waterfall-tender-rate-quote-card-carrier {
      display: grid;
      gap: var(--sp-1) var(--gap-1);
      grid-template-areas:
        'handle arrows avatar carrier-name'
        'blank blank blank warnings';
      justify-content: start;
      align-items: center;
      flex: 1 0 33%;
      width: 100%;
      border-right: var(--divider);
      padding: 0 var(--sp-6) 0 0;

      .drag-handle {
        cursor: grab;
      }

      .move-button {
        height: 24px;
        width: 24px;
        padding: 0;
      }

      .carrier-name-container {
        .waterfall-tender-rate-quote-card-carrier-name {
          font-size: 16px;
          overflow-x: hidden;
          text-overflow: ellipsis;
        }

        small {
          margin-top: var(--sp-2);
        }
      }

      .rate-quote-warnings {
        grid-area: warnings;
      }
    }

    .waterfall-tender-rate-quote-card-highlights {
      flex: 1 0 66%;
      width: 100%;

      .waterfall-tender-rate-quote-card-highlights-cell {
        display: flex;
        align-items: center;
        flex: 1 0 auto;
        width: 25%;

        /*
        flex gap on the container would be better, but for some reason it makes
        the rate cell a different size than the other 3 cells.
      */
        &:not(:first-of-type) {
          padding-left: var(--sp-3);
        }

        &:not(:last-of-type) {
          padding-right: var(--sp-3);
        }
      }

      .waterfall-tender-rate-quote-card-rate {
        border-left: var(--divider);
        padding-left: var(--sp-3);

        &.has-rate {
          .key-value-pair-value {
            color: var(--success-color);
          }
        }
      }
    }
  }
}

@container waterfall-tender-rate-quote (max-width: 800px) {
  .waterfall-tender-rate-quote-card.p-card {
    .p-card-content {
      .waterfall-tender-rate-quote-card-row {
        flex-wrap: wrap;
        row-gap: var(--gap-2);
      }

      .waterfall-tender-rate-quote-card-carrier {
        display: flex;
        gap: var(--gap-1);
        align-items: center;
        border-right: none;
        border-bottom: var(--divider);
        padding: 0 0 var(--sp-3) 0;
      }
    }
  }
}
</style>
